<script>
    import { createEventDispatcher } from 'svelte';
    import Icon from '../icons/Icon.svelte';

    export let show = false;
    export let selectedTable = null;
    export let closeIncludeService = false;
    export let closeDiscount = 0;
    export let splitPeopleCount = 2;
    export let splitCustomers = [];
    export let currentSubtotal = 0;
    export let currentServiceFee = 0;
    export let currentBillTotal = 0;
    export let currentPayments = [];
    export let splitTotalPaid = 0;
    export let splitRemaining = 0;
    export let splitProgressPercent = 0;
    export let equalPerPerson = 0;
    export let customerTotalsMap = {};
    export let isProcessingSplitPayment = false;

    const dispatch = createEventDispatcher();

    // Estado interno do modal de divisão
    let splitTab = 'equal'; // 'equal' | 'items'
    let splitPersonName = 'Pessoa 1';
    let splitPaymentMethod = 'PIX';
    let splitCustomAmount = 0;
    let newCustomerInput = '';
    let selectedCustomerForPayment = null;
    let openAssignDropdown = null;
    let showCustomPaymentForm = false;
    let selectedEqualShareIndex = 1;
    let showSplitHistory = false;

    $: if (show && splitRemaining > 0 && splitCustomAmount === 0) {
        splitCustomAmount = Math.min(splitRemaining, equalPerPerson);
    }

    function formatCurrency(val) {
        const num = parseFloat(val);
        return isNaN(num) ? '0,00' : num.toFixed(2).replace('.', ',');
    }

    function getTimeDiff(dateStr) {
        if (!dateStr) return '0 min';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '0 min';
        const mins = Math.max(1, Math.round((Date.now() - d.getTime()) / 60000));
        if (mins >= 60) {
            const hrs = Math.floor(mins / 60);
            const m = mins % 60;
            return `${hrs}h ${m}m`;
        }
        return `${mins} min`;
    }

    function getDisplayNotes(notes, modifiers) {
        if (!notes) return '';
        if (!modifiers || !Array.isArray(modifiers) || modifiers.length === 0) return notes;
        const parts = notes.split(';').map(p => p.trim()).filter(Boolean);
        const modNames = modifiers.map(m => typeof m === 'object' ? (m.name || '').toLowerCase() : String(m).toLowerCase());
        const filtered = parts.filter(p => !modNames.includes(p.toLowerCase()));
        return filtered.join('; ');
    }

    function handleClose() {
        openAssignDropdown = null;
        dispatch('close');
    }

    function handleTabEqual() {
        splitTab = 'equal';
        openAssignDropdown = null;
        splitPersonName = `Pessoa ${selectedEqualShareIndex || 1}`;
        splitCustomAmount = Math.min(splitRemaining, equalPerPerson);
    }

    function handleTabItems() {
        splitTab = 'items';
        openAssignDropdown = null;
        if (!selectedCustomerForPayment && splitCustomers.length > 0) {
            preparePayForCustomer(splitCustomers[0]);
        }
    }

    function preparePayForCustomer(customer) {
        const custCalc = customerTotalsMap?.[customer] || { remaining: 0, isPaid: false };
        if (custCalc.isPaid || custCalc.remaining <= 0) return;
        selectedCustomerForPayment = customer;
        splitPersonName = customer;
        splitCustomAmount = custCalc.remaining;
        splitPaymentMethod = 'PIX';
    }

    function handleAddCustomer() {
        const name = newCustomerInput.trim();
        if (!name) return;
        dispatch('addCustomer', { name });
        newCustomerInput = '';
    }

    function handleRemoveCustomer(name) {
        dispatch('removeCustomer', { name });
    }

    function handleToggleItemAssignment(itemIndex, customer) {
        dispatch('toggleItemAssignment', { itemIndex, customer });
    }

    function handleQuickPayEqual(personName, method, amount) {
        dispatch('quickPayEqualShare', { personName, method, amount });
    }

    function handleQuickPayCustomer(customer, method, amount) {
        dispatch('quickPayCustomer', { customer, method, amount });
    }

    function handleRecordCustomPayment() {
        dispatch('recordSplitPayment', {
            amount: parseFloat(splitCustomAmount || 0),
            paymentMethod: splitPaymentMethod,
            personName: splitPersonName
        });
    }

    function handleDeletePayment(paymentId) {
        dispatch('deletePayment', { paymentId });
    }

    function handleConfirmClose() {
        dispatch('confirmCloseBill');
    }
</script>

{#if show && selectedTable && selectedTable.comanda}
    <div class="modal-overlay">
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div class="modal-card split-modal" on:click={() => { openAssignDropdown = null; }}>
            <div class="modal-header">
                <div class="comanda-title-group">
                    <span class="comanda-number">#{selectedTable.table_number}</span>
                    <div>
                        <div class="modal-title-wrap">
                            <span class="modal-pretitle">Divisão & Pagamento Parcial</span>
                            <h3>{selectedTable.label}</h3>
                        </div>
                    </div>
                </div>
                <button class="close-btn" on:click={handleClose}><Icon name="x" size={16} /></button>
            </div>

            <!-- NÍVEL 1 & 2: TOTAL DA CONTA & STATUS FINANCEIRO -->
            <div class="split-overview-card">
                <div class="split-total-breakdown">
                    <div class="total-breakdown-item">
                        <span class="lbl">Subtotal</span>
                        <strong>R$ {formatCurrency(currentSubtotal)}</strong>
                    </div>
                    <label class="total-breakdown-item service-toggle" title="Incluir taxa de serviço de 10%">
                        <input type="checkbox" bind:checked={closeIncludeService} />
                        <span class="lbl">Serviço 10%</span>
                        <strong>R$ {formatCurrency(currentServiceFee)}</strong>
                    </label>
                    {#if closeDiscount > 0}
                        <div class="total-breakdown-item text-discount">
                            <span class="lbl">Desconto</span>
                            <strong>- R$ {formatCurrency(closeDiscount)}</strong>
                        </div>
                    {/if}
                    <div class="total-breakdown-item total-final">
                        <span class="lbl">Total da Mesa</span>
                        <strong class="total-final-val">R$ {formatCurrency(currentBillTotal)}</strong>
                    </div>
                </div>

                <div class="split-status-strip">
                    <div class="status-val-box">
                        <span class="status-lbl">Já Recebido</span>
                        <strong class="status-val text-success">R$ {formatCurrency(splitTotalPaid)}</strong>
                    </div>
                    <div class="status-sep"></div>
                    <div class="status-val-box">
                        <span class="status-lbl">Saldo Restante</span>
                        <strong class="status-val text-orange">R$ {formatCurrency(splitRemaining)}</strong>
                    </div>
                    <div class="status-progress-wrap">
                        <div class="status-progress-meta">
                            <span class="status-pct-badge">{splitProgressPercent}% pago</span>
                            <span class="status-count">{splitRemaining <= 0 ? 'Quitado' : `${currentPayments.length} pagamento(s)`}</span>
                        </div>
                        <div class="status-progress-bar">
                            <div class="status-progress-fill" style="width: {splitProgressPercent}%"></div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- NÍVEL 3: ABAS DE MODALIDADE DE DIVISÃO -->
            <div class="split-tabs-nav">
                <button 
                    type="button"
                    class="split-tab-btn" 
                    class:active={splitTab === 'equal'} 
                    on:click={handleTabEqual}
                >
                    <Icon name="users" size={15} />
                    <span>Divisão Igualitária (Rápida)</span>
                </button>
                <button 
                    type="button"
                    class="split-tab-btn" 
                    class:active={splitTab === 'items'} 
                    on:click={handleTabItems}
                >
                    <Icon name="file-text" size={15} />
                    <span>Divisão por Itens & Consumo</span>
                </button>
            </div>

            <div class="modal-body split-body">
                {#if splitTab === 'equal'}
                    <!-- ABA 1: DIVISÃO IGUALITÁRIA (CARDS COM BAIXA RÁPIDA EM 1 CLIQUE) -->
                    <div class="equal-split-wrap">
                        <div class="split-stepper-box">
                            <div class="stepper-label">
                                <strong>Dividir igualmente em:</strong>
                                <small>Altere o número de pessoas para recalcular as cotas individuais</small>
                            </div>
                            <div class="stepper-controls">
                                <button 
                                    type="button"
                                    class="stepper-btn" 
                                    on:click={() => { 
                                        if (splitPeopleCount > 1) { 
                                            splitPeopleCount--; 
                                            splitCustomAmount = Math.min(splitRemaining, equalPerPerson); 
                                        } 
                                    }}
                                >−</button>
                                <span class="stepper-count">{splitPeopleCount} pessoas</span>
                                <button 
                                    type="button" 
                                    class="stepper-btn" 
                                    on:click={() => { 
                                        splitPeopleCount++; 
                                        splitCustomAmount = Math.min(splitRemaining, equalPerPerson); 
                                    }}
                                >+</button>
                            </div>
                            <div class="split-per-person-badge">
                                <span>Valor por pessoa:</span>
                                <strong>R$ {formatCurrency(equalPerPerson)}</strong>
                            </div>
                        </div>

                        <!-- Fichas Rápidas de Pagamento das Cotas em Cards -->
                        <div class="equal-shares-section">
                            <div class="equal-shares-header">
                                <h4>Cotas da Mesa ({splitPeopleCount})</h4>
                                <small>Clique na forma de pagamento para dar baixa imediata de cada cliente</small>
                            </div>

                            <div class="equal-shares-grid">
                                {#each Array(splitPeopleCount) as _, idx}
                                    {@const shareIndex = idx + 1}
                                    {@const sharePayment = currentPayments[idx] || null}
                                    {@const isSharePaid = !!sharePayment}
                                    {@const shareName = isSharePaid ? sharePayment.person_name : `Pessoa ${shareIndex}`}
                                    {@const shareVal = isSharePaid ? sharePayment.amount : (idx === splitPeopleCount - 1 && splitRemaining > 0 ? splitRemaining : Math.min(splitRemaining, equalPerPerson))}
                                    <div class="equal-share-card" class:is-paid={isSharePaid}>
                                        <div class="share-card-top">
                                            <div class="share-card-title">
                                                <span class="share-avatar">{shareIndex}</span>
                                                <strong>{shareName}</strong>
                                            </div>
                                            {#if isSharePaid}
                                                <span class="badge-share-paid"><Icon name="check" size={12} /> Quitado</span>
                                            {:else}
                                                <span class="share-amount">R$ {formatCurrency(shareVal)}</span>
                                            {/if}
                                        </div>

                                        {#if isSharePaid}
                                            <div class="share-paid-meta">
                                                <span class="pay-method-tag">{sharePayment.payment_method}</span>
                                                <strong class="pay-val-tag">R$ {formatCurrency(sharePayment.amount)}</strong>
                                            </div>
                                        {:else}
                                            <div class="share-actions-row">
                                                <button 
                                                    type="button" 
                                                    class="btn-share-pay pix"
                                                    disabled={isProcessingSplitPayment || splitRemaining <= 0}
                                                    on:click={() => handleQuickPayEqual(shareName, 'PIX', shareVal)}
                                                    title="Pagar no PIX"
                                                >
                                                    <Icon name="zap" size={12} color="#059669" />
                                                    <span>PIX</span>
                                                </button>
                                                <button 
                                                    type="button" 
                                                    class="btn-share-pay card"
                                                    disabled={isProcessingSplitPayment || splitRemaining <= 0}
                                                    on:click={() => handleQuickPayEqual(shareName, 'Cartão de Crédito', shareVal)}
                                                    title="Pagar no Cartão"
                                                >
                                                    <Icon name="credit-card" size={12} color="#2563EB" />
                                                    <span>Cartão</span>
                                                </button>
                                                <button 
                                                    type="button" 
                                                    class="btn-share-pay cash"
                                                    disabled={isProcessingSplitPayment || splitRemaining <= 0}
                                                    on:click={() => handleQuickPayEqual(shareName, 'Dinheiro', shareVal)}
                                                    title="Pagar em Dinheiro"
                                                >
                                                    <Icon name="dollar-sign" size={12} color="#16A34A" />
                                                    <span>Dinheiro</span>
                                                </button>
                                            </div>
                                        {/if}
                                    </div>
                                {/each}
                            </div>

                            <!-- Opção para Pagamento com Valor Avulso / Manual -->
                            <div class="custom-pay-accordion">
                                <button 
                                    type="button" 
                                    class="btn-toggle-custom-pay" 
                                    on:click={() => showCustomPaymentForm = !showCustomPaymentForm}
                                >
                                    <Icon name={showCustomPaymentForm ? 'chevron-up' : 'chevron-down'} size={12} />
                                    <span>{showCustomPaymentForm ? 'Ocultar pagamento manual' : 'Precisa registrar valor avulso ou outra forma? Clique aqui'}</span>
                                </button>

                                {#if showCustomPaymentForm}
                                    <div class="split-pay-form custom-open-box">
                                        <div class="split-form-grid">
                                            <div class="field">
                                                <label for="split-person">Identificação / Pessoa:</label>
                                                <input 
                                                    id="split-person" 
                                                    type="text" 
                                                    placeholder="Ex: Carlos, Ana" 
                                                    bind:value={splitPersonName} 
                                                />
                                            </div>
                                            <div class="field">
                                                <label for="split-method">Forma de Pagamento:</label>
                                                <select id="split-method" bind:value={splitPaymentMethod}>
                                                    <option value="PIX">PIX (Chave ou QR Code)</option>
                                                    <option value="Cartão de Crédito">Cartão de Crédito</option>
                                                    <option value="Cartão de Débito">Cartão de Débito</option>
                                                    <option value="Dinheiro">Dinheiro (Espécie)</option>
                                                    <option value="VR / Vale Refeição">VR / Vale Refeição</option>
                                                </select>
                                            </div>
                                            <div class="field">
                                                <label for="split-amount">Valor a Receber (R$):</label>
                                                <input 
                                                    id="split-amount" 
                                                    type="number" 
                                                    step="0.01" 
                                                    min="0.01" 
                                                    max={splitRemaining} 
                                                    bind:value={splitCustomAmount} 
                                                />
                                            </div>
                                        </div>
                                        <div class="split-quick-pills">
                                            <span>Atalhos:</span>
                                            <button type="button" class="btn-pill" on:click={() => splitCustomAmount = Math.min(splitRemaining, equalPerPerson)}>Cota Exata (R$ {formatCurrency(equalPerPerson)})</button>
                                            <button type="button" class="btn-pill" on:click={() => splitCustomAmount = splitRemaining}>Saldo Restante (R$ {formatCurrency(splitRemaining)})</button>
                                        </div>
                                        <button 
                                            type="button" 
                                            class="btn-primary btn-record-pay" 
                                            disabled={isProcessingSplitPayment || splitRemaining <= 0}
                                            on:click={handleRecordCustomPayment}
                                        >
                                            {isProcessingSplitPayment ? 'Registrando...' : `Confirmar Recebimento de R$ ${formatCurrency(splitCustomAmount || 0)}`}
                                        </button>
                                    </div>
                                {/if}
                            </div>
                        </div>
                    </div>
                {:else}
                    <!-- ABA 2: DIVISÃO POR ITENS & CONSUMO -->
                    <div class="items-split-wrap">
                        <!-- Gerenciador de Pessoas da Mesa -->
                        <div class="split-customers-manager">
                            <div class="cust-manager-header">
                                <strong>Pessoas na Mesa ({splitCustomers.length})</strong>
                                <small>Adicione os clientes para atribuir o consumo de cada um</small>
                            </div>
                            <div class="cust-tags-row">
                                {#each splitCustomers as c}
                                    <span class="cust-tag">
                                        <Icon name="user" size={12} />
                                        <span>{c}</span>
                                        {#if splitCustomers.length > 1}
                                            <button type="button" on:click={() => handleRemoveCustomer(c)} title="Remover cliente">✕</button>
                                        {/if}
                                    </span>
                                {/each}
                            </div>
                            <div class="add-cust-inline">
                                <input 
                                    type="text" 
                                    placeholder="Nome do cliente (ex: Carlos, Mariana)..." 
                                    bind:value={newCustomerInput}
                                    on:keydown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustomer(); } }}
                                />
                                <button type="button" class="btn-add-cust" on:click={handleAddCustomer}>
                                    + Adicionar Pessoa
                                </button>
                            </div>
                        </div>

                        <!-- Layout em 2 Colunas -->
                        <div class="split-two-cols">
                            <!-- Coluna 1: Itens da Comanda com Atribuição Inline -->
                            <div class="items-col">
                                <div class="col-heading">
                                    <h4>Itens da Comanda</h4>
                                    <small>Clique em "Consumo" para selecionar quem consumiu</small>
                                </div>
                                <div class="items-split-scroll">
                                    <table class="comanda-table split-table">
                                        <thead>
                                            <tr>
                                                <th>Qtd & Item</th>
                                                <th>Total</th>
                                                <th>Consumo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {#each selectedTable.comanda.items || [] as item, idx}
                                                <tr>
                                                    <td class="name-cell">
                                                        <strong>{item.qty}x {item.name}</strong>
                                                        {#if item.modifiers && Array.isArray(item.modifiers) && item.modifiers.length > 0}
                                                            <div class="item-modifiers-wrap">
                                                                {#each item.modifiers as mod}
                                                                    {#if (typeof mod === 'object' && mod && mod.name) || (typeof mod === 'string' && mod.trim())}
                                                                        <span class="item-modifier-chip">
                                                                            + {typeof mod === 'object' ? mod.name : mod}
                                                                            {#if typeof mod === 'object' && Number(mod.price) > 0}
                                                                                <span class="modifier-price">(+R$ {formatCurrency(mod.price)})</span>
                                                                            {/if}
                                                                        </span>
                                                                    {/if}
                                                                {/each}
                                                            </div>
                                                        {/if}
                                                        {#if getDisplayNotes(item.notes, item.modifiers)}
                                                            <small class="item-notes">{getDisplayNotes(item.notes, item.modifiers)}</small>
                                                        {/if}
                                                    </td>
                                                    <td class="total-cell">
                                                        R$ {formatCurrency(item.price * item.qty)}
                                                    </td>
                                                    <td class="assign-cell">
                                                        <button 
                                                            type="button" 
                                                            class="assign-selector-btn" 
                                                            class:is-open={openAssignDropdown === idx}
                                                            on:click|stopPropagation={() => openAssignDropdown = (openAssignDropdown === idx ? null : idx)}
                                                        >
                                                            {#if !item.assigned_to || item.assigned_to.length === 0}
                                                                <span class="badge-all">Todos da Mesa</span>
                                                            {:else if item.assigned_to.length === 1}
                                                                <span class="badge-single"><Icon name="user" size={12} /> {item.assigned_to[0]}</span>
                                                            {:else}
                                                                <span class="badge-multi"><Icon name="users" size={12} /> {item.assigned_to.length} pessoas</span>
                                                            {/if}
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                                                {#if openAssignDropdown === idx}
                                                                    <polyline points="18 15 12 9 6 15"/>
                                                                {:else}
                                                                    <polyline points="6 9 12 15 18 9"/>
                                                                {/if}
                                                            </svg>
                                                        </button>
                                                    </td>
                                                </tr>
                                                {#if openAssignDropdown === idx}
                                                    <tr class="assign-expand-row">
                                                        <td colspan="3">
                                                            <!-- svelte-ignore a11y-click-events-have-key-events -->
                                                            <div class="assign-inline-panel" on:click|stopPropagation>
                                                                <div class="assign-inline-header">
                                                                    <span>Quem consumiu <strong>{item.name}</strong>?</span>
                                                                    <button type="button" class="btn-close-inline" on:click={() => openAssignDropdown = null}>
                                                                        ✕ Fechar
                                                                    </button>
                                                                </div>
                                                                <div class="assign-inline-chips">
                                                                    <button 
                                                                        type="button" 
                                                                        class="chip-assign" 
                                                                        class:selected={!item.assigned_to || item.assigned_to.length === 0}
                                                                        on:click={() => handleToggleItemAssignment(idx, 'all')}
                                                                    >
                                                                        <span class="chip-check">{(!item.assigned_to || item.assigned_to.length === 0) ? '✓' : '○'}</span>
                                                                        <span>Dividido p/ Todos da Mesa</span>
                                                                    </button>
                                                                    {#each splitCustomers as c}
                                                                        {@const isAssigned = item.assigned_to && item.assigned_to.includes(c)}
                                                                        <button 
                                                                            type="button" 
                                                                            class="chip-assign" 
                                                                            class:selected={isAssigned}
                                                                            on:click={() => handleToggleItemAssignment(idx, c)}
                                                                        >
                                                                            <span class="chip-check">{isAssigned ? '✓' : '+'}</span>
                                                                            <span>{c}</span>
                                                                        </button>
                                                                    {/each}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                {/if}
                                            {/each}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <!-- Coluna 2: Extrato por Pessoa com Pagamento Direto no Card -->
                            <div class="customers-col">
                                <div class="col-heading">
                                    <h4>Subtotal por Pessoa</h4>
                                    <small>Recebimento individual com 1 clique</small>
                                </div>
                                <div class="cust-cards-stack">
                                    {#each splitCustomers as c (c)}
                                        {@const calc = customerTotalsMap?.[c] || { items: [], isPaid: false, remaining: 0, total: 0, paid: 0 }}
                                        <div class="cust-card-v2" class:is-paid={calc.isPaid}>
                                            <div class="cust-card-top-v2">
                                                <div class="cust-name-box">
                                                    <strong>{c}</strong>
                                                    <small>{calc.items?.length || 0} item(ns) atribuído(s)</small>
                                                </div>
                                                {#if calc.isPaid}
                                                    <span class="cust-badge-paid"><Icon name="check" size={12} /> Quitado</span>
                                                {:else}
                                                    <span class="cust-amount-v2">R$ {formatCurrency(calc.remaining)}</span>
                                                {/if}
                                            </div>

                                            {#if calc.items && calc.items.length > 0}
                                                <div class="cust-items-detail">
                                                    {#each calc.items as it}
                                                        <div class="cust-item-mini">
                                                            <span>{it.qty}x {it.name} {it.isShared ? '(dividido)' : ''}</span>
                                                            <strong>R$ {formatCurrency(it.share)}</strong>
                                                        </div>
                                                    {/each}
                                                </div>
                                            {/if}

                                            {#if calc.isPaid}
                                                <div class="cust-paid-banner">
                                                    <Icon name="check" size={13} /> Total de R$ {formatCurrency(calc.paid || calc.total)} pago
                                                </div>
                                            {:else if calc.remaining > 0}
                                                <div class="cust-quick-pay-row">
                                                    <span class="quick-lbl">Receber:</span>
                                                    <div class="mini-pay-chips">
                                                        <button 
                                                            type="button" 
                                                            class="btn-mini-pay pix" 
                                                            disabled={isProcessingSplitPayment || splitRemaining <= 0}
                                                            on:click={() => handleQuickPayCustomer(c, 'PIX', calc.remaining)}
                                                        >
                                                            PIX
                                                        </button>
                                                        <button 
                                                            type="button" 
                                                            class="btn-mini-pay card" 
                                                            disabled={isProcessingSplitPayment || splitRemaining <= 0}
                                                            on:click={() => handleQuickPayCustomer(c, 'Cartão de Crédito', calc.remaining)}
                                                        >
                                                            Cartão
                                                        </button>
                                                        <button 
                                                            type="button" 
                                                            class="btn-mini-pay cash" 
                                                            disabled={isProcessingSplitPayment || splitRemaining <= 0}
                                                            on:click={() => handleQuickPayCustomer(c, 'Dinheiro', calc.remaining)}
                                                        >
                                                            Dinheiro
                                                        </button>
                                                    </div>
                                                </div>
                                            {/if}
                                        </div>
                                    {/each}
                                </div>
                            </div>
                        </div>
                    </div>
                {/if}

                <!-- HISTÓRICO DE PAGAMENTOS DISCRETO / RECOLHÍVEL -->
                {#if currentPayments.length > 0}
                    <div class="split-history-accordion">
                        <button 
                            type="button" 
                            class="btn-toggle-history" 
                            on:click={() => showSplitHistory = !showSplitHistory}
                        >
                            <span>{showSplitHistory ? '▲ Ocultar histórico de pagamentos' : `▼ Ver pagamentos já efetuados nesta mesa (${currentPayments.length})`}</span>
                        </button>

                        {#if showSplitHistory}
                            <div class="split-history-section">
                                <div class="split-payments-list">
                                    {#each currentPayments as pay}
                                        <div class="split-payment-item">
                                            <div class="pay-info">
                                                <strong>{pay.person_name}</strong>
                                                <span class="pay-meta">{pay.payment_method} • {getTimeDiff(pay.paid_at)}</span>
                                            </div>
                                            <div class="pay-actions">
                                                <span class="pay-val">R$ {formatCurrency(pay.amount)}</span>
                                                <button 
                                                    type="button" 
                                                    class="btn-pay-void" 
                                                    on:click={() => handleDeletePayment(pay.id)} 
                                                    title="Estornar este pagamento"
                                                ><Icon name="x" size={11} /> Estornar</button>
                                            </div>
                                        </div>
                                    {/each}
                                </div>
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>

            <div class="modal-footer">
                <button class="btn-cancel" on:click={handleClose}>
                    Voltar à Comanda
                </button>
                {#if splitRemaining <= 0}
                    <button class="btn-primary" on:click={handleConfirmClose}>
                        <Icon name="check" size={14} /> Liberar Mesa
                    </button>
                {/if}
            </div>
        </div>
    </div>
{/if}

<style>
    .modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.6);
        backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        animation: fadeIn 0.15s ease-out;
    }

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }

    .modal-card.split-modal {
        background: #FFFFFF;
        border-radius: 16px;
        max-width: 960px;
        width: 95%;
        max-height: calc(100vh - 32px);
        max-height: calc(100dvh - 32px);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        padding: 20px 24px;
        box-sizing: border-box;
        border: 1px solid #E2E8F0;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        animation: slideUp 0.2s cubic-bezier(0.16, 1, 0.3, 1);
    }

    @keyframes slideUp {
        from { transform: translateY(12px) scale(0.98); opacity: 0; }
        to { transform: translateY(0) scale(1); opacity: 1; }
    }

    .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 12px;
        padding-bottom: 8px;
        border-bottom: 1px solid #E2E8F0;
    }

    .comanda-title-group {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .comanda-number {
        font-size: 16px;
        font-weight: 900;
        color: #FF6A00;
        background: #FFF4EC;
        padding: 4px 10px;
        border-radius: 8px;
        border: 1px solid #FED7AA;
    }

    .modal-title-wrap {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .modal-pretitle {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #FF6A00;
    }

    .modal-title-wrap h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 800;
        color: #0F172A;
    }

    .close-btn {
        background: transparent;
        border: none;
        color: #64748B;
        cursor: pointer;
        padding: 6px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s ease;
    }

    .close-btn:hover {
        background: #F1F5F9;
        color: #0F172A;
    }

    /* OVERVIEW CARD */
    .split-overview-card {
        background: #F8FAFC;
        border: 1px solid #E2E8F0;
        border-radius: 12px;
        padding: 12px 16px;
        margin-bottom: 12px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        flex-shrink: 0;
    }

    .split-total-breakdown {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 12px;
        padding-bottom: 8px;
        border-bottom: 1px solid #E2E8F0;
    }

    .total-breakdown-item {
        display: flex;
        flex-direction: column;
        gap: 1px;
    }

    .total-breakdown-item .lbl {
        font-size: 10.5px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #64748B;
    }

    .total-breakdown-item strong {
        font-size: 13.5px;
        color: #1E293B;
    }

    .total-breakdown-item.service-toggle {
        cursor: pointer;
        user-select: none;
        display: inline-flex;
        flex-direction: row;
        align-items: center;
        gap: 6px;
        background: #FFFFFF;
        border: 1px solid #CBD5E1;
        padding: 3px 8px;
        border-radius: 6px;
    }

    .total-breakdown-item.service-toggle input {
        margin: 0;
        cursor: pointer;
        accent-color: #FF6A00;
    }

    .total-breakdown-item.text-discount strong {
        color: #059669;
    }

    .total-breakdown-item.total-final {
        margin-left: auto;
        text-align: right;
    }

    .total-final-val {
        font-size: 19px !important;
        font-weight: 900 !important;
        color: #FF6A00 !important;
    }

    .split-status-strip {
        display: flex;
        align-items: center;
        gap: 14px;
    }

    .status-val-box {
        display: flex;
        flex-direction: column;
        gap: 1px;
    }

    .status-lbl {
        font-size: 10.5px;
        font-weight: 700;
        text-transform: uppercase;
        color: #64748B;
    }

    .status-val {
        font-size: 15px;
        font-weight: 800;
    }

    .status-val.text-success {
        color: #059669;
    }

    .status-val.text-orange {
        color: #FF6A00;
    }

    .status-sep {
        width: 1px;
        height: 26px;
        background: #CBD5E1;
    }

    .status-progress-wrap {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
        margin-left: auto;
    }

    .status-progress-meta {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .status-pct-badge {
        font-size: 10.5px;
        font-weight: 800;
        color: #FF6A00;
        background: #FFF4EC;
        padding: 1px 7px;
        border-radius: 12px;
    }

    .status-count {
        font-size: 11px;
        color: #64748B;
        font-weight: 600;
    }

    .status-progress-bar {
        height: 6px;
        background: #E2E8F0;
        border-radius: 999px;
        overflow: hidden;
    }

    .status-progress-fill {
        height: 100%;
        background: linear-gradient(90deg, #FF8C33 0%, #FF6A00 100%);
        border-radius: 999px;
        transition: width 0.3s ease;
    }

    /* ABAS */
    .split-tabs-nav {
        display: flex;
        gap: 8px;
        margin-bottom: 12px;
        border-bottom: 1px solid #E2E8F0;
        padding-bottom: 10px;
        flex-shrink: 0;
    }

    .split-tab-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: #F8FAFC;
        border: 1px solid #CBD5E1;
        color: #475569;
        padding: 8px 16px;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .split-tab-btn:hover {
        background: #F1F5F9;
        color: #0F172A;
    }

    .split-tab-btn.active {
        background: #FFF4EC;
        color: #FF6A00;
        border-color: #FF6A00;
        box-shadow: 0 2px 6px rgba(255, 106, 0, 0.15);
    }

    .split-body {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 14px;
        padding-right: 2px;
    }

    .equal-split-wrap {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .split-stepper-box {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 12px;
        background: #FFFFFF;
        border: 1px solid #CBD5E1;
        border-radius: 10px;
        padding: 10px 14px;
    }

    .stepper-label strong {
        display: block;
        font-size: 13px;
        color: #0F172A;
    }

    .stepper-label small {
        color: #64748B;
        font-size: 11.5px;
    }

    .stepper-controls {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .stepper-btn {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #F1F5F9;
        border: 1px solid #CBD5E1;
        color: #0F172A;
        border-radius: 6px;
        font-size: 16px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s;
    }

    .stepper-btn:hover {
        background: #E2E8F0;
        border-color: #94A3B8;
    }

    .stepper-count {
        font-size: 13.5px;
        font-weight: 800;
        color: #0F172A;
        min-width: 75px;
        text-align: center;
    }

    .split-per-person-badge {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        background: #FFF4EC;
        border: 1px solid #FED7AA;
        padding: 5px 12px;
        border-radius: 8px;
    }

    .split-per-person-badge span {
        font-size: 10.5px;
        color: #C2410C;
        font-weight: 600;
    }

    .split-per-person-badge strong {
        font-size: 14px;
        color: #FF6A00;
        font-weight: 800;
    }

    /* CARDS DE COTAS IGUAIS */
    .equal-shares-section {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .equal-shares-header h4 {
        margin: 0;
        font-size: 13px;
        font-weight: 800;
        color: #0F172A;
    }

    .equal-shares-header small {
        font-size: 11px;
        color: #64748B;
    }

    .equal-shares-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: 10px;
    }

    .equal-share-card {
        background: #FFFFFF;
        border: 1px solid #CBD5E1;
        border-radius: 10px;
        padding: 10px 12px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        transition: all 0.2s ease;
    }

    .equal-share-card.is-paid {
        background: #F0FDF4;
        border-color: #A7F3D0;
    }

    .share-card-top {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .share-card-title {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .share-avatar {
        width: 24px;
        height: 24px;
        background: #FFF4EC;
        color: #FF6A00;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 800;
        border: 1px solid #FED7AA;
    }

    .equal-share-card.is-paid .share-avatar {
        background: #D1FAE5;
        color: #059669;
        border-color: #A7F3D0;
    }

    .share-amount {
        font-size: 13.5px;
        font-weight: 800;
        color: #FF6A00;
    }

    .badge-share-paid {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 11px;
        font-weight: 800;
        color: #059669;
        background: #D1FAE5;
        padding: 2px 8px;
        border-radius: 6px;
    }

    .share-paid-meta {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 11.5px;
        color: #059669;
        padding-top: 4px;
        border-top: 1px dashed #D1FAE5;
    }

    .share-actions-row {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 6px;
    }

    .btn-share-pay {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        background: #F8FAFC;
        border: 1px solid #CBD5E1;
        color: #334155;
        font-size: 11px;
        font-weight: 700;
        padding: 6px 4px;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.15s ease;
        text-align: center;
    }

    .btn-share-pay:hover:not(:disabled) {
        background: #FF6A00;
        border-color: #FF6A00;
        color: #FFFFFF;
    }

    .btn-share-pay.pix:hover:not(:disabled) {
        background: #059669;
        border-color: #059669;
        color: #FFFFFF;
    }

    /* PAGAMENTO MANUAL */
    .custom-pay-accordion {
        margin-top: 6px;
    }

    .btn-toggle-custom-pay {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        background: transparent;
        border: none;
        color: #64748B;
        font-size: 11.5px;
        font-weight: 600;
        cursor: pointer;
        padding: 4px 0;
        text-decoration: underline;
    }

    .btn-toggle-custom-pay:hover {
        color: #FF6A00;
    }

    .custom-open-box {
        background: #FFFFFF;
        border: 1.5px solid #CBD5E1;
        border-radius: 10px;
        padding: 12px 14px;
        margin-top: 8px;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .split-form-grid {
        display: grid;
        grid-template-columns: 1.2fr 1fr 1fr;
        gap: 10px;
    }

    .field {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .field label {
        font-size: 11.5px;
        font-weight: 700;
        color: #475569;
    }

    .field input,
    .field select {
        padding: 7px 10px;
        background: #F8FAFC;
        border: 1.5px solid #CBD5E1;
        border-radius: 6px;
        font-size: 12.5px;
        color: #0F172A;
    }

    .field input:focus,
    .field select:focus {
        outline: none;
        border-color: #FF6A00;
        background: #FFFFFF;
    }

    .split-quick-pills {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 11.5px;
        color: #64748B;
    }

    .btn-pill {
        background: #F1F5F9;
        border: 1px solid #CBD5E1;
        color: #334155;
        font-size: 11px;
        font-weight: 600;
        padding: 3px 8px;
        border-radius: 4px;
        cursor: pointer;
    }

    .btn-pill:hover {
        background: #E2E8F0;
        color: #0F172A;
    }

    .btn-record-pay {
        padding: 8px 14px;
        font-size: 12.5px;
        font-weight: 800;
    }

    /* ABA 2: ITENS & CONSUMO */
    .items-split-wrap {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .split-customers-manager {
        background: #F8FAFC;
        border: 1px solid #E2E8F0;
        border-radius: 10px;
        padding: 10px 12px;
    }

    .cust-manager-header {
        margin-bottom: 6px;
    }

    .cust-manager-header strong {
        display: block;
        font-size: 12.5px;
        color: #0F172A;
    }

    .cust-manager-header small {
        color: #64748B;
        font-size: 11px;
    }

    .cust-tags-row {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-bottom: 8px;
    }

    .cust-tag {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        background: #EFF6FF;
        border: 1px solid #BFDBFE;
        color: #1E40AF;
        padding: 2px 8px;
        border-radius: 20px;
        font-size: 11.5px;
        font-weight: 700;
    }

    .cust-tag button {
        background: transparent;
        border: none;
        color: #60A5FA;
        cursor: pointer;
        padding: 0;
        font-size: 11px;
        font-weight: 900;
    }

    .cust-tag button:hover {
        color: #DC2626;
    }

    .add-cust-inline {
        display: flex;
        gap: 8px;
    }

    .add-cust-inline input {
        flex: 1;
        background: #FFFFFF;
        border: 1px solid #CBD5E1;
        padding: 6px 10px;
        border-radius: 6px;
        font-size: 12px;
        color: #0F172A;
    }

    .btn-add-cust {
        background: #FFF4EC;
        border: 1px solid #FED7AA;
        color: #FF6A00;
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s ease;
        white-space: nowrap;
    }

    .btn-add-cust:hover {
        background: #FFEDD5;
        border-color: #FB923C;
    }

    .split-two-cols {
        display: grid;
        grid-template-columns: 1.15fr 0.85fr;
        gap: 14px;
        align-items: start;
    }

    .col-heading h4 {
        margin: 0;
        font-size: 13px;
        font-weight: 800;
        color: #0F172A;
    }

    .col-heading small {
        font-size: 11px;
        color: #64748B;
        display: block;
        margin-bottom: 6px;
    }

    .items-split-scroll {
        max-height: 280px;
        overflow-y: auto;
        border: 1px solid #E2E8F0;
        border-radius: 8px;
        background: #FFFFFF;
    }

    .split-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px;
    }

    .split-table th {
        background: #F8FAFC;
        padding: 8px 10px;
        text-align: left;
        font-weight: 700;
        color: #475569;
        border-bottom: 1px solid #E2E8F0;
    }

    .split-table td {
        padding: 8px 10px;
        border-bottom: 1px solid #F1F5F9;
        vertical-align: top;
    }

    .name-cell strong {
        display: block;
        color: #0F172A;
    }

    .item-modifiers-wrap {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin-top: 3px;
    }

    .item-modifier-chip {
        font-size: 10.5px;
        color: #64748B;
        background: #F1F5F9;
        padding: 1px 5px;
        border-radius: 4px;
    }

    .modifier-price {
        color: #059669;
        font-weight: 600;
    }

    .item-notes {
        display: block;
        font-size: 11px;
        color: #94A3B8;
        font-style: italic;
        margin-top: 2px;
    }

    .total-cell {
        white-space: nowrap;
        font-weight: 700;
        color: #1E293B;
    }

    .assign-cell {
        text-align: right;
    }

    .assign-selector-btn {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        background: #F8FAFC;
        border: 1px solid #CBD5E1;
        border-radius: 6px;
        padding: 4px 8px;
        font-size: 11px;
        font-weight: 700;
        color: #334155;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .assign-selector-btn:hover,
    .assign-selector-btn.is-open {
        background: #FFF4EC;
        border-color: #FF6A00;
        color: #FF6A00;
    }

    .badge-all { color: #64748B; }
    .badge-single { color: #0369A1; }
    .badge-multi { color: #C2410C; }

    .assign-expand-row td {
        padding: 0 !important;
        border-bottom: 2px solid #FED7AA !important;
        background: #FFFDF9;
    }

    .assign-inline-panel {
        padding: 10px 12px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .assign-inline-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 11.5px;
        color: #475569;
    }

    .assign-inline-header strong {
        color: #0F172A;
    }

    .btn-close-inline {
        background: transparent;
        border: none;
        color: #94A3B8;
        font-size: 11px;
        font-weight: 700;
        cursor: pointer;
        padding: 2px 6px;
        border-radius: 4px;
    }

    .btn-close-inline:hover {
        background: #F1F5F9;
        color: #334155;
    }

    .assign-inline-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }

    .chip-assign {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 5px 10px;
        border-radius: 6px;
        border: 1.5px solid #CBD5E1;
        background: #FFFFFF;
        font-size: 11.5px;
        font-weight: 600;
        color: #334155;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .chip-assign:hover {
        border-color: #FF6A00;
        background: #FFF9F5;
    }

    .chip-assign.selected {
        border-color: #FF6A00;
        background: #FFF4EC;
        color: #C2410C;
        font-weight: 800;
        box-shadow: 0 1px 3px rgba(255, 106, 0, 0.15);
    }

    .chip-check {
        font-weight: 800;
        font-size: 11px;
    }

    /* CARDS DE CLIENTES */
    .cust-cards-stack {
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-height: 280px;
        overflow-y: auto;
        padding-right: 4px;
    }

    .cust-card-v2 {
        background: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 8px;
        padding: 10px 12px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        transition: all 0.2s ease;
    }

    .cust-card-v2.is-paid {
        background: #F0FDF4;
        border-color: #A7F3D0;
    }

    .cust-card-top-v2 {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .cust-name-box strong {
        display: block;
        font-size: 13px;
        color: #0F172A;
    }

    .cust-name-box small {
        font-size: 10.5px;
        color: #64748B;
    }

    .cust-amount-v2 {
        font-size: 14px;
        font-weight: 900;
        color: #FF6A00;
    }

    .cust-badge-paid {
        font-size: 11px;
        font-weight: 800;
        color: #059669;
        background: #D1FAE5;
        padding: 2px 7px;
        border-radius: 6px;
    }

    .cust-items-detail {
        background: #F8FAFC;
        border-radius: 6px;
        padding: 6px 8px;
        display: flex;
        flex-direction: column;
        gap: 3px;
        max-height: 70px;
        overflow-y: auto;
    }

    .cust-item-mini {
        display: flex;
        justify-content: space-between;
        font-size: 10.5px;
        color: #475569;
    }

    .cust-paid-banner {
        font-size: 11px;
        font-weight: 700;
        color: #059669;
        background: #D1FAE5;
        padding: 4px 8px;
        border-radius: 6px;
        text-align: center;
    }

    .cust-quick-pay-row {
        display: flex;
        align-items: center;
        gap: 6px;
        padding-top: 4px;
        border-top: 1px dashed #E2E8F0;
    }

    .quick-lbl {
        font-size: 10.5px;
        font-weight: 700;
        color: #64748B;
    }

    .mini-pay-chips {
        display: flex;
        gap: 4px;
        flex: 1;
    }

    .btn-mini-pay {
        flex: 1;
        background: #F1F5F9;
        border: 1px solid #CBD5E1;
        color: #334155;
        font-size: 10.5px;
        font-weight: 700;
        padding: 4px 0;
        border-radius: 5px;
        cursor: pointer;
        transition: all 0.15s ease;
        text-align: center;
    }

    .btn-mini-pay:hover:not(:disabled) {
        background: #FF6A00;
        border-color: #FF6A00;
        color: #FFFFFF;
    }

    .btn-mini-pay.pix:hover:not(:disabled) {
        background: #059669;
        border-color: #059669;
        color: #FFFFFF;
    }

    /* HISTÓRICO DE PAGAMENTOS */
    .split-history-accordion {
        margin-top: 4px;
        border-top: 1px dashed #CBD5E1;
        padding-top: 8px;
    }

    .btn-toggle-history {
        background: transparent;
        border: none;
        color: #64748B;
        font-size: 11.5px;
        font-weight: 700;
        cursor: pointer;
        padding: 4px 0;
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .btn-toggle-history:hover {
        color: #0F172A;
    }

    .split-history-section {
        margin-top: 8px;
        background: #F8FAFC;
        border: 1px solid #E2E8F0;
        border-radius: 8px;
        padding: 8px 12px;
    }

    .split-payments-list {
        display: flex;
        flex-direction: column;
        gap: 6px;
        max-height: 120px;
        overflow-y: auto;
    }

    .split-payment-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 12px;
        padding: 4px 0;
        border-bottom: 1px solid #F1F5F9;
    }

    .split-payment-item:last-child {
        border-bottom: none;
    }

    .pay-info {
        display: flex;
        flex-direction: column;
        gap: 1px;
    }

    .pay-info strong {
        color: #0F172A;
    }

    .pay-meta {
        font-size: 10.5px;
        color: #64748B;
    }

    .pay-actions {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .pay-val {
        font-weight: 800;
        color: #059669;
    }

    .btn-pay-void {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        background: transparent;
        border: 1px solid #FCA5A5;
        color: #DC2626;
        font-size: 10.5px;
        font-weight: 700;
        padding: 2px 6px;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.15s;
    }

    .btn-pay-void:hover {
        background: #FEF2F2;
    }

    /* FOOTER */
    .modal-footer {
        padding: 14px 20px 4px 20px;
        border-top: 1px solid #E2E8F0;
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        background: #FFFFFF;
    }

    .btn-cancel {
        padding: 9px 16px;
        background: #FFFFFF;
        border: 1.5px solid #CBD5E1;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 700;
        color: #475569;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .btn-cancel:hover {
        background: #F1F5F9;
        color: #0F172A;
    }

    .btn-primary {
        padding: 9px 18px;
        background: #FF6A00;
        border: 1.5px solid #FF6A00;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 800;
        color: #FFFFFF;
        cursor: pointer;
        box-shadow: 0 2px 4px rgba(255, 106, 0, 0.2);
        transition: all 0.2s ease;
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }

    .btn-primary:hover:not(:disabled) {
        background: #E65F00;
        border-color: #E65F00;
        box-shadow: 0 4px 8px rgba(255, 106, 0, 0.3);
    }

    .btn-primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    @media (max-width: 767.98px) {
        .modal-card.split-modal {
            width: 100vw !important;
            max-width: 100vw !important;
            height: 100vh !important;
            height: 100dvh !important;
            max-height: 100dvh !important;
            border-radius: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            position: fixed !important;
            inset: 0 !important;
            z-index: 10001 !important;
            background: #FFFFFF !important;
        }

        .split-modal .modal-header {
            padding: 12px 14px !important;
            border-bottom: 1px solid #E2E8F0 !important;
            background: #FFFFFF !important;
            flex-shrink: 0 !important;
        }

        .split-modal .modal-body {
            flex: 1 !important;
            overflow-y: auto !important;
            padding: 14px 14px 100px 14px !important;
            -webkit-overflow-scrolling: touch !important;
            box-sizing: border-box !important;
        }

        .split-modal .modal-footer {
            position: fixed !important;
            bottom: 0 !important;
            left: 0 !important;
            right: 0 !important;
            background: #FFFFFF !important;
            border-top: 1.5px solid #E2E8F0 !important;
            padding: 10px 14px calc(10px + env(safe-area-inset-bottom, 0px)) 14px !important;
            box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.1) !important;
            z-index: 10002 !important;
            display: flex !important;
            gap: 8px !important;
        }

        .split-modal .modal-footer button {
            min-height: 48px !important;
            font-size: 13.5px !important;
            font-weight: 800 !important;
            border-radius: 10px !important;
            touch-action: manipulation;
        }

        .split-modal .modal-footer .btn-primary {
            flex: 1 !important;
        }

        .split-two-cols {
            display: flex !important;
            flex-direction: column !important;
            gap: 18px !important;
            width: 100% !important;
        }

        .items-col,
        .customers-col {
            width: 100% !important;
            min-width: 0 !important;
        }

        .items-split-scroll {
            max-height: none !important;
            border: none !important;
            background: transparent !important;
        }

        .comanda-table.split-table {
            display: flex !important;
            flex-direction: column !important;
            width: 100% !important;
            border-collapse: separate !important;
            gap: 10px !important;
        }

        .comanda-table.split-table thead {
            display: none !important;
        }

        .comanda-table.split-table tbody {
            display: flex !important;
            flex-direction: column !important;
            width: 100% !important;
            gap: 10px !important;
        }

        .comanda-table.split-table tbody tr:not(.assign-expand-row) {
            display: flex !important;
            flex-direction: column !important;
            width: 100% !important;
            background: #FFFFFF !important;
            border: 1.5px solid #E2E8F0 !important;
            border-radius: 12px !important;
            padding: 12px 14px !important;
            box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05) !important;
            gap: 8px !important;
            box-sizing: border-box !important;
        }

        .comanda-table.split-table td {
            display: block !important;
            padding: 0 !important;
            border: none !important;
            width: 100% !important;
            text-align: left !important;
        }

        .comanda-table.split-table td.name-cell {
            order: 1 !important;
            width: 100% !important;
            padding-bottom: 2px !important;
        }

        .comanda-table.split-table td.name-cell strong {
            font-size: 15px !important;
            font-weight: 800 !important;
            color: #0F172A !important;
            word-break: break-word !important;
            display: block !important;
        }

        .comanda-table.split-table td.total-cell {
            order: 2 !important;
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            font-size: 15px !important;
            font-weight: 800 !important;
            color: #0F172A !important;
            padding-bottom: 6px !important;
            border-bottom: 1px dashed #E2E8F0 !important;
            width: 100% !important;
        }

        .comanda-table.split-table td.total-cell::before {
            content: "Valor do Item:" !important;
            font-size: 12px !important;
            font-weight: 600 !important;
            color: #64748B !important;
        }

        .comanda-table.split-table td.assign-cell {
            order: 3 !important;
            text-align: left !important;
            width: 100% !important;
        }

        .assign-selector-btn {
            width: 100% !important;
            min-height: 44px !important;
            height: 44px !important;
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            padding: 0 14px !important;
            font-size: 13px !important;
            font-weight: 700 !important;
            border-radius: 8px !important;
            border: 1.5px solid #CBD5E1 !important;
            background: #F8FAFC !important;
            box-sizing: border-box !important;
            touch-action: manipulation !important;
        }

        .assign-expand-row {
            display: block !important;
            width: 100% !important;
            margin-top: -4px !important;
            margin-bottom: 4px !important;
        }

        .assign-expand-row td {
            display: block !important;
            width: 100% !important;
            background: transparent !important;
            border: none !important;
        }

        .assign-inline-panel {
            background: #FFFDF7 !important;
            border: 1.5px solid #FED7AA !important;
            border-radius: 10px !important;
            padding: 12px 14px !important;
            box-shadow: 0 4px 14px rgba(255, 106, 0, 0.08) !important;
            box-sizing: border-box !important;
        }

        .assign-inline-chips {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 8px !important;
            margin-top: 8px !important;
        }

        .chip-assign {
            min-height: 42px !important;
            padding: 0 14px !important;
            font-size: 13px !important;
            font-weight: 700 !important;
            border-radius: 8px !important;
            touch-action: manipulation !important;
        }

        .cust-cards-stack {
            display: flex !important;
            flex-direction: column !important;
            gap: 12px !important;
            width: 100% !important;
        }

        .cust-card-v2 {
            width: 100% !important;
            border-radius: 12px !important;
            padding: 14px !important;
            box-sizing: border-box !important;
            border: 1.5px solid #E2E8F0 !important;
        }

        .cust-card-top-v2 {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            margin-bottom: 8px !important;
        }

        .mini-pay-chips {
            display: grid !important;
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 8px !important;
            width: 100% !important;
            margin-top: 8px !important;
        }

        .btn-mini-pay {
            min-height: 44px !important;
            height: 44px !important;
            font-size: 13px !important;
            font-weight: 800 !important;
            border-radius: 8px !important;
            touch-action: manipulation !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
        }
    }
</style>
